import { useRef, useState } from 'react'
import { useAuthStore } from './authStore'
import { apiClient } from '../../shared/api/client'
import { getPartCatalog, importPartCatalog } from '../../shared/api/partCatalog.api'
import { toast } from '../../shared/ui/toastStore'
import { loadXlsx } from '../../shared/utils/loadXlsx'
import type { AuthUser } from '@crm/shared'

export function AccountSettingsPage() {
  const { user, fetchMe } = useAuthStore()
  const isAdmin = user?.role === 'ADMIN'

  // ── Настройки аккаунта ──
  const [name, setName] = useState(user?.name ?? '')
  const [phone, setPhone] = useState(user?.phone ?? '')
  const [personalEmail, setPersonalEmail] = useState(user?.personalEmail ?? '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      await apiClient.patch<{ success: true; data: AuthUser }>('/auth/me', {
        name: name.trim() || null,
        phone: phone.trim() || null,
        personalEmail: personalEmail.trim() || null,
      })
      await fetchMe()
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch {
      setError('Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  // ── Каталог партномеров (только ADMIN) ──
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [exportLoading, setExportLoading] = useState(false)
  const [importLoading, setImportLoading] = useState(false)
  const [importResult, setImportResult] = useState<{ inserted: number; skipped: number } | null>(
    null,
  )
  const [importError, setImportError] = useState('')

  const handleExport = async () => {
    setExportLoading(true)
    try {
      const { items: data } = await getPartCatalog(undefined, 100000)
      const XLSX = await loadXlsx()
      const ws = XLSX.utils.json_to_sheet(
        data.map((r) => ({ 'Part Number': r.partNumber, Brand: r.brand })),
      )
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Catalog')
      XLSX.writeFile(wb, 'part-catalog.xlsx')
    } catch {
      // Раньше catch не было — падение запроса или загрузки чанка молча уходило
      // в unhandled rejection, кнопка просто ничего не делала.
      toast.error('Не удалось выгрузить каталог в Excel')
    } finally {
      setExportLoading(false)
    }
  }

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImportLoading(true)
    setImportResult(null)
    setImportError('')
    const finishImport = () => {
      setImportLoading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
    // Чанк xlsx тянем отдельным try: у его ошибки уже есть свой русский текст
    // («обновите страницу»), а «Ошибка при разборе файла» тут вводит в
    // заблуждение — файл до разбора даже не дошёл.
    let XLSX: Awaited<ReturnType<typeof loadXlsx>>
    try {
      XLSX = await loadXlsx()
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Не удалось загрузить модуль Excel')
      finishImport()
      return
    }
    try {
      const ab = await file.arrayBuffer()
      const wb = XLSX.read(new Uint8Array(ab), { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws)
      const rows = raw
        .map((r) => ({
          partNumber: String(r['Part Number'] ?? r['partNumber'] ?? r['part_number'] ?? '').trim(),
          brand: String(r['Brand'] ?? r['brand'] ?? '').trim(),
        }))
        .filter((r) => r.partNumber && r.brand)
      if (rows.length === 0) {
        setImportError('Не найдено строк с полями "Part Number" и "Brand"')
        return
      }
      const result = await importPartCatalog(rows)
      setImportResult(result)
    } catch {
      setImportError('Ошибка при разборе файла')
    } finally {
      finishImport()
    }
  }

  return (
    <div className="max-w-md space-y-6">
      <h1 className="text-xl font-semibold text-gray-900">Настройки аккаунта</h1>

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        {/* Email — read only */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Почта</label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 select-all">
            {user?.email}
          </div>
          <p className="text-xs text-gray-400">Почту изменить нельзя</p>
        </div>

        {/* Name */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Имя менеджера
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Напр. Иван Петров"
            maxLength={100}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <p className="text-xs text-gray-400">
            Отображается в таблице в колонке «Сопровод». Можно оставить пустым.
          </p>
        </div>

        {/* Телефон */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Телефон
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Напр. +7 (912) 345-67-89"
            maxLength={32}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <p className="text-xs text-gray-400">
            Подставляется в строку «Телефон» при создании КП. Если пусто — ставится общий номер
            компании.
          </p>
        </div>

        {/* Личная почта — попадает в КП (Excel и PDF) строкой «Почта» под телефоном */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Личная почта
          </label>
          <input
            type="email"
            value={personalEmail}
            onChange={(e) => setPersonalEmail(e.target.value)}
            placeholder="Напр. ivan@micro-hub.ru"
            maxLength={255}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <p className="text-xs text-gray-400">
            Подставляется в КП (Excel и PDF) строкой «Почта» под телефоном. Можно оставить пустым.
          </p>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          {saved && <span className="text-sm text-green-600">Сохранено</span>}
        </div>
      </div>

      {/* Каталог партномеров — только для ADMIN */}
      {isAdmin && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Каталог партномеров</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Автоматически пополняется при вводе партномеров менеджерами. Дубли исключаются.
            </p>
          </div>

          {/* Экспорт */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              disabled={exportLoading}
              className="px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {exportLoading ? 'Выгрузка...' : 'Экспорт в Excel'}
            </button>
            <span className="text-xs text-gray-400">Скачать весь каталог (.xlsx)</span>
          </div>

          {/* Импорт */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importLoading}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                {importLoading ? 'Загрузка...' : 'Импорт из Excel'}
              </button>
              <span className="text-xs text-gray-400">Колонки: "Part Number", "Brand"</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleImportFile}
            />
            {importResult && (
              <p className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                Добавлено: {importResult.inserted}, пропущено дублей: {importResult.skipped}
              </p>
            )}
            {importError && (
              <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{importError}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
